import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Gauge, Scale } from "lucide-react";
import { CollectorAvatar } from "@/components/CollectorAvatar";
import { RebalanceDialog } from "@/components/company/RebalanceDialog";

const TAGS = {
  overloaded: { label: "Overloaded", cls: "bg-[#B91C1C]/10 text-[#B91C1C]", bar: "bg-[#B91C1C]/70" },
  balanced: { label: "Balanced", cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]", bar: "bg-[#2D6A4F]/70" },
  light: { label: "Light", cls: "bg-[#E9C46A]/30 text-[#8A5A00]", bar: "bg-[#E9C46A]" },
  not_receiving: { label: "Not receiving leads", cls: "bg-[#1E3F33]/8 text-[#5C6B64]", bar: "bg-[#1E3F33]/20" },
};

const ago = (iso) => {
  const h = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 3600000));
  return h < 24 ? `${h}h` : `${Math.round(h / 24)}d`;
};

const Row = ({ r, max, active, onSelect, onRebalance, canRebalance }) => (
  <li className={`flex flex-wrap sm:flex-nowrap items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${active ? "bg-[#2D6A4F]/10 ring-1 ring-[#2D6A4F]/30" : "hover:bg-[#F0EFEA]/70"}`} data-testid={`workload-row-${r.id}`}>
    <button type="button" onClick={() => onSelect(r.id)} className="flex items-center gap-3 min-w-0 flex-1 text-left" data-testid={`workload-select-${r.id}`} title="Show only this teammate's leads">
      <CollectorAvatar name={r.name} photo={r.photo_url} size="w-8 h-8" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2"><span className="text-sm font-medium text-[#1E3F33] truncate">{r.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${TAGS[r.status].cls}`} data-testid={`workload-status-${r.id}`}>{TAGS[r.status].label}</span></span>
        <span className="mt-1.5 block h-1.5 rounded-full bg-[#1E3F33]/8 overflow-hidden"><span className={`block h-full rounded-full ${TAGS[r.status].bar}`} style={{ width: `${max ? Math.max(4, (r.open / max) * 100) : 0}%` }} /></span>
      </span>
    </button>
    <span className="flex items-center gap-4 text-xs text-[#5C6B64] shrink-0 w-full sm:w-auto pl-11 sm:pl-0">
      <span data-testid={`workload-open-${r.id}`}><b className="text-[#1E3F33] text-sm">{r.open}</b> open</span>
      <span className={r.stale ? "text-[#B91C1C]" : ""} data-testid={`workload-oldest-${r.id}`}>{r.oldest_open_at ? `oldest ${ago(r.oldest_open_at)}` : "—"}</span>
      <span data-testid={`workload-contacted-${r.id}`}>{r.contacted} contacted</span>
      {canRebalance && r.status === "overloaded" && (
        <button onClick={() => onRebalance(r)} className="text-xs font-medium text-[#CC5A3A] hover:underline inline-flex items-center gap-1" data-testid={`workload-rebalance-${r.id}`}><Scale className="w-3 h-3" /> Rebalance</button>
      )}
    </span>
  </li>
);

export const TeamWorkload = ({ version, selected, onSelect, onChanged }) => {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null);
  const load = useCallback(() => api.get("/company/workload").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, version]);
  const close = useCallback(() => setTarget(null), []);
  if (!data || !data.collectors.length) return null;
  const max = Math.max(...data.collectors.map((r) => r.open), 0);
  const canRebalance = data.collectors.filter((r) => r.can_receive).length > 1;

  return (
    <div className="card-soft mb-6" data-testid="team-workload">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
        <div>
          <h3 className="font-heading text-lg text-[#1E3F33] flex items-center gap-2"><Gauge className="w-5 h-5 text-[#CC5A3A]" /> Workload</h3>
          <p className="text-xs text-[#8A9A86]">Open = new leads not contacted yet · team average {data.team_average} · "Overloaded" = over 1.5× average or a lead waiting {data.stale_hours}h+. Click a teammate to filter the leads below.</p>
        </div>
        {selected && <button onClick={() => onSelect("")} className="text-xs text-[#2D6A4F] hover:underline" data-testid="workload-clear-filter">Show everyone</button>}
      </div>
      <ul className="space-y-1">
        {data.collectors.map((r) => (
          <Row key={r.id} r={r} max={max} active={selected === r.id} onSelect={(id) => onSelect(selected === id ? "" : id)} onRebalance={setTarget} canRebalance={canRebalance} />
        ))}
      </ul>
      <RebalanceDialog collector={target} onClose={close} onDone={async () => { await onChanged(); load(); }} />
    </div>
  );
};
